import { MenuItem, Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";

import { ContractType, IRules, IRulesRegister, VigencyType, AdvanceNoticeType } from "../../Setup.types";
import { Container, Row, TextField, SelectForm, DatePicker } from "./Rules.styled";

import { maskPercentage, parseDate } from "./Rules.utils";
import { formatCNPJ } from "utils/is-valid-cnpj";
import { useAuth } from "contexts/auth";
import { useContractStatus } from "v2/pages/OperationalPanel/useContractStatus";
import { ContractStatus } from "v2/pages/OperationalPanel/contractStatus";

interface IProps {
  rules: IRules;
  handleChange: (payload: Partial<IRules>) => void;
}

export function RulesTabRegister({ rules, handleChange }: IProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { user } = useAuth();
  const { getContractStatus, setContractStatus } = useContractStatus();

  // Ponto 3 do plano de melhorias de lógica (2026-08-10): "Estado" é
  // deliberadamente separado de `rules`/`handleChangeRegister` — não deve
  // entrar no payload do `POST /v2/new/workspaces/create` (não afeta o
  // banco de dados). Persiste do mesmo jeito que o "Silenciar" do Painel
  // Operacional: localStorage + arquivo versionado via PR.
  const sellerName = user?.organization?.name ?? "";
  const contractStatus = getContractStatus(workspaceId, sellerName, rules.rulesRegister.buyerFederalTaxId ?? "");
  /** Função responsável especificamente por receber os valores alterados e formatar corretamente para handleChange. */
  function handleChangeRegister(payload: Partial<IRulesRegister>) {
    const updatedRegisterFields = { ...rules.rulesRegister, ...payload };

    handleChange({ ...rules, rulesRegister: updatedRegisterFields });
  }

  return (
    <Container>
      <Row>
        <TextField
          label="Cliente"
          disabled
          value={rules.rulesRegister.buyerOrganizationName}
          onChange={(e) => handleChangeRegister({ buyerOrganizationName: e.target.value })}
        />
        <Tooltip title="Digite o CNPJ no formato 00.000.000/0000-00" placement="top">
          <TextField
            label="CNPJ"
            disabled
            value={formatCNPJ(rules.rulesRegister.buyerFederalTaxId, false)}
            onChange={(e) => handleChangeRegister({ buyerFederalTaxId: e.target.value.replace(/\D/g, '') })}
            />
        </Tooltip>
        <TextField
          label="Operadora"
          disabled
          value={rules.rulesRegister.supplierOrganizationName}
          onChange={(e) => handleChangeRegister({ supplierOrganizationName: e.target.value })}
        />
        <TextField
          label="Nome do produto"
          disabled
          value={rules.rulesRegister.productName}
          onChange={(e) => handleChangeRegister({ productName: e.target.value })}
        />
      </Row>
      <Row>
        <TextField
          label="Tipo da operadora"
          value={rules.rulesRegister.supplierOrganizationType}
          onChange={(e) => handleChangeRegister({ supplierOrganizationType: e.target.value })}
        />
        <TextField
          required
          label="Nº contrato/apólice"
          value={rules.rulesRegister.policyContractNumber}
          onChange={(e) => handleChangeRegister({ policyContractNumber: e.target.value })}
        />
        <TextField
          label="Valor do Produto c/ IOF"
          value={maskPercentage(rules.rulesRegister.productValueIof)}
          onChange={(e) => handleChangeRegister({ productValueIof: e.target.value })}
        />
        <SelectForm
          label="Modelo de contratação"
          value={rules.rulesRegister.type}
          onChange={(e) => handleChangeRegister({ type: e.target.value as ContractType })}
        >
          <MenuItem value={"COMPULSORIO"}>Compulsório</MenuItem>
          <MenuItem value={"LIVRE_ADESAO"}>Livre Adesão</MenuItem>
        </SelectForm>
      </Row>
      <Row>
        <TextField
          label="Nº de vidas inicial"
          type="number"
          inputProps={{ min: 0 }}
          value={rules.rulesRegister.peopleInitialNumber}
          onChange={(e) => handleChangeRegister({ peopleInitialNumber: Number(e.target.value) ?? 0 })}
        />
        <SelectForm
          label="Vigência do contrato no fornecedor"
          value={rules.rulesRegister.vigency}
          onChange={(e) => handleChangeRegister({ vigency: e.target.value as VigencyType })}
        >
          <MenuItem value={"12_MONTHS"}>12 meses</MenuItem>
          <MenuItem value={"24_MONTHS"}>24 meses</MenuItem>
          <MenuItem value={"36_MONTHS"}>36 meses</MenuItem>
        </SelectForm>
        <DatePicker
          label="Data de início da vigência"
          format="DD / MM / YYYY"
          value={parseDate(rules.rulesRegister.vigencyStartDate)}
          onChange={(e) => handleChangeRegister({ vigencyStartDate: e?.toDate() })}
        />
        <SelectForm
          label="Período aviso prévio"
          value={rules.rulesRegister.advanceNotice}
          onChange={(e) => handleChangeRegister({ advanceNotice: e.target.value as AdvanceNoticeType })}
        >
          <MenuItem value={"60_DAYS"}>60 dias</MenuItem>
        </SelectForm>
      </Row>
      <Row>
        <TextField
          label="Email para envio de faturamento"
          value={rules.rulesRegister.invoiceEmail}
          onChange={(e) => handleChangeRegister({ invoiceEmail: e.target.value })}
        />
        <SelectForm
          label="Estado"
          value={contractStatus}
          onChange={(e) => setContractStatus(workspaceId, e.target.value as ContractStatus, user?.email ?? "desconhecido")}
        >
          <MenuItem value={"ATIVO"}>Ativo</MenuItem>
          <MenuItem value={"INATIVO"}>Inativo</MenuItem>
          <MenuItem value={"EM_CONFIGURACAO"}>Em configuração</MenuItem>
        </SelectForm>
      </Row>
    </Container>
  );
}
